/**
 * Client for the helpdesk-team delegation endpoints
 * (`/api/workspaces/:slug/helpdesk/{members,invitations,roles}`). A
 * `helpdesk_admin` manages the desk's roster + its scoped custom roles here
 * WITHOUT workspace `member:*`. Thin wrappers over `apiRequest`; the backend
 * pins every call to the helpdesk scope, so these clients never need to.
 */

import { apiRequest } from "../agents/api/client";
import type { WorkflowPerm } from "../settings/api";

export interface HelpdeskMember {
  id: string;
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePictureUrl: string | null;
  role: string;
  joinedAt: string;
}

export interface HelpdeskInvitation {
  id: string;
  email: string;
  role: string;
  invitedBy: { id: string; email: string };
  expiresAt: string;
  createdAt: string;
}

export interface HelpdeskBuiltinRole {
  name: string;
  permissions: string[];
}
export interface HelpdeskCustomRole {
  id: string;
  name: string;
  permissions: string[];
}
export interface HelpdeskRoleList {
  builtin: HelpdeskBuiltinRole[];
  custom: HelpdeskCustomRole[];
  /** Every permission slug a helpdesk-scoped custom role may grant. */
  ceiling: string[];
}

export const helpdeskTeamKeys = {
  members: (slug: string) => ["helpdesk-team", slug, "members"] as const,
  invitations: (slug: string) => ["helpdesk-team", slug, "invitations"] as const,
  roles: (slug: string) => ["helpdesk-team", slug, "roles"] as const,
  roleWorkflow: (slug: string, roleId: string) =>
    ["helpdesk-team", slug, roleId, "workflow-perms"] as const,
};

const base = (slug: string) => `/api/workspaces/${slug}/helpdesk`;

// ----- members -----

export function listHelpdeskMembers(slug: string): Promise<HelpdeskMember[]> {
  return apiRequest<HelpdeskMember[]>("GET", `${base(slug)}/members`);
}
export function changeHelpdeskMemberRole(
  slug: string,
  memberId: string,
  role: string,
): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>("PATCH", `${base(slug)}/members/${memberId}`, { role });
}
export function removeHelpdeskMember(slug: string, memberId: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>("DELETE", `${base(slug)}/members/${memberId}`);
}

// ----- invitations -----

export function listHelpdeskInvitations(slug: string): Promise<HelpdeskInvitation[]> {
  return apiRequest<HelpdeskInvitation[]>("GET", `${base(slug)}/invitations`);
}
export function inviteHelpdeskMember(
  slug: string,
  body: { email: string; role: string },
): Promise<HelpdeskInvitation> {
  return apiRequest<HelpdeskInvitation>("POST", `${base(slug)}/invitations`, body);
}
export function revokeHelpdeskInvitation(
  slug: string,
  invitationId: string,
): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>("DELETE", `${base(slug)}/invitations/${invitationId}`);
}

// ----- roles -----

export function listHelpdeskRoles(slug: string): Promise<HelpdeskRoleList> {
  return apiRequest<HelpdeskRoleList>("GET", `${base(slug)}/roles`);
}
export function createHelpdeskRole(
  slug: string,
  body: { name: string; permissions: string[] },
): Promise<HelpdeskCustomRole> {
  return apiRequest<HelpdeskCustomRole>("POST", `${base(slug)}/roles`, body);
}
export function updateHelpdeskRole(
  slug: string,
  id: string,
  body: { name: string; permissions: string[] },
): Promise<HelpdeskCustomRole> {
  return apiRequest<HelpdeskCustomRole>("PATCH", `${base(slug)}/roles/${id}`, body);
}
export function deleteHelpdeskRole(slug: string, id: string): Promise<{ repointed: number }> {
  return apiRequest<{ repointed: number }>("DELETE", `${base(slug)}/roles/${id}`);
}

// ----- per-workflow permissions on a desk role -----
// (No per-query counterpart: the desk holds no query:* slugs.)

export function getHelpdeskRoleWorkflowPerms(
  slug: string,
  roleId: string,
): Promise<WorkflowPerm[]> {
  return apiRequest<WorkflowPerm[]>("GET", `${base(slug)}/roles/${roleId}/workflow-permissions`);
}
export function setHelpdeskRoleWorkflowPerms(
  slug: string,
  roleId: string,
  entries: WorkflowPerm[],
): Promise<WorkflowPerm[]> {
  return apiRequest<WorkflowPerm[]>("PUT", `${base(slug)}/roles/${roleId}/workflow-permissions`, {
    entries,
  });
}

/** Human label for a helpdesk role value (built-in key or a custom name). */
export function helpdeskRoleLabel(role: string, customNames?: ReadonlyMap<string, string>): string {
  const custom = customNames?.get(role);
  if (custom) return custom;
  if (role === "helpdesk_admin") return "Helpdesk Admin";
  if (role === "helpdesk_member") return "Helpdesk Member";
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
}
